import { memo, useCallback } from 'react'

import { Save, Warning } from '@mui/icons-material'
import { Box, Checkbox, FormControlLabel, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { VideoObject } from '@nativewaves/nw-cloud-sdk/content'
import { useSnackbar } from 'notistack'
import { Controller, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'

import { ConfirmButton } from '@shared/components/Confirm/ConfirmButton'
import { useCloseHandler } from '@shared/components/MaterialUIEnhancements/Dialog'
import { DialogFormContent } from '@shared/layouts'
import { DurationField } from 'components/DurationField'
import { useVideoObjectUpdateMutation } from 'hooks/mutations/content'

type FormStructure = {
  deleteAfter: number
  useParentLifecyclePolicy: boolean
  isAutodelete: boolean
}

type LifecyclePolicyDialogProps = {
  objectData: VideoObject | VideoObject[]
  onSuccess?: () => void
}

const LifecyclePolicyDialogRaw = ({ objectData, onSuccess }: LifecyclePolicyDialogProps) => {
  const { t } = useTranslation(['common', 'entity', 'domain'])
  const { enqueueSnackbar } = useSnackbar()
  const handleClose = useCloseHandler()

  const isArray = Array.isArray(objectData)
  const count = isArray ? objectData.length : 1

  const videoObjectUpdateMutation = useVideoObjectUpdateMutation()
  const { mutate: updateVideoObject } = videoObjectUpdateMutation

  const { handleSubmit, control, watch } = useForm<FormStructure>({
    defaultValues: {
      useParentLifecyclePolicy: isArray ? false : objectData.useParentLifecyclePolicy,
      deleteAfter: isArray ? 0 : objectData.lifecyclePolicy.deleteAfter || 0,
      isAutodelete: isArray ? false : !!objectData.lifecyclePolicy.deleteAfter,
    },
  })

  const parentLifecyclePolicy = watch('useParentLifecyclePolicy')
  const isAutodelete = watch('isAutodelete')

  const handleFormSubmit = useCallback(
    async (formData: FormStructure) => {
      const deleteAfter = formData.isAutodelete ? formData.deleteAfter : 0

      try {
        if (isArray) {
          await Promise.allSettled(
            objectData.map((object) =>
              updateVideoObject({
                videoId: object.id,
                videoObjectEdit: {
                  ...object,
                  useParentLifecyclePolicy: formData.useParentLifecyclePolicy,
                  lifecyclePolicy: {
                    deleteAfter: formData.useParentLifecyclePolicy ? object.lifecyclePolicy.deleteAfter : deleteAfter,
                  },
                },
              }),
            ),
          )
        } else {
          await updateVideoObject({
            videoId: objectData.id,
            videoObjectEdit: {
              ...objectData,
              useParentLifecyclePolicy: formData.useParentLifecyclePolicy,
              lifecyclePolicy: {
                deleteAfter: formData.useParentLifecyclePolicy ? objectData.lifecyclePolicy.deleteAfter : deleteAfter,
              },
            },
          })
        }
        enqueueSnackbar(t('domain:Workspace.Video.updateLifecyclePolicySuccess', { count }), {
          variant: 'success',
        })

        onSuccess?.()
        handleClose()
      } catch (_) {
        enqueueSnackbar(t('domain:Workspace.Video.updateLifecyclePolicyError', { count }), { variant: 'error' })
      }
    },
    [count, enqueueSnackbar, handleClose, isArray, objectData, onSuccess, t, updateVideoObject],
  )

  return (
    <DialogFormContent
      Illustration={null}
      title={t('domain:Workspace.Video.updateLifecyclePolicy', { count })}
      SaveButton={
        <ConfirmButton
          sx={{ ml: 'auto' }}
          startIcon={<Save />}
          variant="text"
          onConfirm={handleSubmit(handleFormSubmit)}
          staticLabel={t('save')}
        />
      }
    >
      <Controller
        name="useParentLifecyclePolicy"
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={<Checkbox checked={field.value} {...field} />}
            label={t('domain:Workspace.Video.useParentLifecyclePolicy')}
            labelPlacement="end"
            sx={{ width: 'fit-content', ml: -1.5, gap: 1, mb: 2 }}
          />
        )}
      />
      <Controller
        name="isAutodelete"
        control={control}
        render={({ field }) => (
          <ToggleButtonGroup
            disabled={parentLifecyclePolicy}
            value={field.value}
            color="primary"
            size="small"
            sx={{ mb: 4 }}
          >
            <ToggleButton
              value={false}
              selected={!parentLifecyclePolicy && !field.value}
              onClick={() => field.onChange(false)}
              sx={{ textTransform: 'capitalize' }}
            >
              {t('domain:Workspace.Video.noExpiration')}
            </ToggleButton>
            <ToggleButton
              value
              selected={!parentLifecyclePolicy && field.value}
              onClick={() => field.onChange(true)}
              sx={{ textTransform: 'capitalize' }}
            >
              {t('domain:Workspace.Video.autoDeletion')}
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      />
      <Controller
        name="deleteAfter"
        control={control}
        render={({ field, fieldState }) => (
          <DurationField
            defaultValue={field.value}
            hasError={!!fieldState.error}
            disabled={parentLifecyclePolicy || !isAutodelete}
            label={t('deleteAfter')}
            {...field}
          />
        )}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxWidth: '480px', mt: 5 }}>
        <Typography>{t('domain:Workspace.Video.updatePolicyDescription')}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
          <Warning color="warning" />
          <Typography>
            <Trans ns="domain" i18nKey="Workspace.Video.updatePolicyWarning" />
          </Typography>
        </Box>
      </Box>
    </DialogFormContent>
  )
}

export const LifecyclePolicyDialog = memo(LifecyclePolicyDialogRaw)
